import {
  AssistantRequestError,
  streamChat,
  type AssistantFailure
} from "@/lib/order-assistant/client";
import { MAX_MESSAGE_LENGTH } from "@/lib/order-assistant/contract";
import {
  canSend,
  conversationReducer,
  isResendable,
  type ConversationState
} from "@/lib/order-assistant/conversation";
import {
  loadConversation,
  saveConversation
} from "@/lib/order-assistant/storage";
import { orderAssistantQueryKeys } from "@/queries/order-assistant";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode
} from "react";

/** The supplier product a card opened in the drawer's read-only detail view. */
export type SupplierDetailTarget = {
  supplierProductId: string;
  /** The variant the card showed, preselected in the detail view. */
  supplierVariantId: string;
  /** The card was not mapped to a master product: show the badge here too. */
  notOnSalonStocker: boolean;
  /** The card's currency: the detail route sends none. */
  currency: string;
};

type OrderAssistantContextValue = {
  state: ConversationState;
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Whether the composer would accept a message right now. */
  sendable: boolean;
  send: (text: string) => void;
  /** Ask the failed or cut-short last turn again. */
  retry: () => void;
  /** Send the composer's message again after the service refused it. */
  resendDraft: () => void;
  startNew: () => void;
  setDraft: (draft: string) => void;
  dismissNotice: () => void;
  supplierDetail: SupplierDetailTarget | null;
  openSupplierDetail: (target: SupplierDetailTarget) => void;
  closeSupplierDetail: () => void;
};

const OrderAssistantContext = createContext<OrderAssistantContextValue | null>(
  null
);

/** How often a streaming turn is written to `sessionStorage`. */
const SAVE_INTERVAL_MS = 1000;

let idCounter = 0;
function nextId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
}

type ProviderProps = {
  /** The signed-in customer. The stored conversation is keyed to it. */
  customerId: string;
  children: ReactNode;
};

/**
 * Owns the conversation for the signed-in salon, above the drawer: closing the
 * drawer, navigating inside the app and re-opening it all find the same
 * transcript, and a turn keeps streaming while the drawer is shut. Mount it
 * keyed by `customerId` so a different salon starts from nothing.
 */
export function OrderAssistantProvider({
  customerId,
  children
}: ProviderProps) {
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(
    conversationReducer,
    customerId,
    loadConversation
  );
  const [open, setOpen] = useState(false);
  const [supplierDetail, setSupplierDetail] =
    useState<SupplierDetailTarget | null>(null);

  const stateRef = useRef(state);
  stateRef.current = state;
  const abortRef = useRef<AbortController | null>(null);

  // Saved as it changes, so a reload mid-turn restores the question (the reply
  // comes back marked failed and resendable - `loadConversation`). While a turn
  // streams, state changes on every delta, so saving is throttled: once now,
  // then at most once per `SAVE_INTERVAL_MS` with the latest state. A settled
  // conversation is saved at once and cancels any pending save.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!state.inFlight) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      saveConversation(customerId, state);
      return;
    }
    if (saveTimerRef.current) return;
    saveConversation(customerId, state);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      if (stateRef.current.inFlight) {
        saveConversation(customerId, stateRef.current);
      }
    }, SAVE_INTERVAL_MS);
  }, [customerId, state]);
  // Not on unmount: logout clears the stored conversation, and a save that fires
  // after it would bring it back.
  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    []
  );

  // A turn is cancelled when the assistant goes away (logout, navigating out of
  // the signed-in shell).
  useEffect(() => () => abortRef.current?.abort(), []);

  // `disabled` (section 7.2): the entry point goes away and the drawer closes.
  useEffect(() => {
    if (!state.disabled) return;
    queryClient.setQueryData(orderAssistantQueryKeys.status(customerId), {
      enabled: false
    });
    setOpen(false);
  }, [state.disabled, queryClient, customerId]);

  // The message limit lifts by itself at the reset time.
  const resetAt = state.limit?.resetAt;
  useEffect(() => {
    if (!resetAt) return;
    const delay = Date.parse(resetAt) - Date.now();
    if (Number.isNaN(delay) || delay <= 0) {
      dispatch({ type: "limit_expired" });
      return;
    }
    // setTimeout cannot wait longer than ~24.8 days; a reset is hours away.
    const timer = setTimeout(
      () => dispatch({ type: "limit_expired" }),
      Math.min(delay, 2 ** 31 - 1)
    );
    return () => clearTimeout(timer);
  }, [resetAt]);

  const run = useCallback(async (text: string, draft: string) => {
    const controller = new AbortController();
    abortRef.current = controller;
    dispatch({
      type: "send",
      text,
      draft,
      userId: nextId("u"),
      assistantId: nextId("a")
    });

    let terminal = false;
    try {
      for await (const event of streamChat({
        message: text,
        sessionId: stateRef.current.sessionId,
        signal: controller.signal
      })) {
        if (event.type === "done" || event.type === "error") terminal = true;
        dispatch({ type: "event", event });
      }
      if (!terminal) dispatch({ type: "stream_closed" });
    } catch (error) {
      if (controller.signal.aborted) return;
      // Only the failure's code is kept; the error itself (which could, for a
      // malformed stream, quote the answer) is never reported or logged.
      const failure: AssistantFailure =
        error instanceof AssistantRequestError
          ? error.failure
          : { code: "network", fields: {} };
      dispatch({ type: "failure", failure });
    }
  }, []);

  const send = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (text === "" || text.length > MAX_MESSAGE_LENGTH) return;
      if (!canSend(stateRef.current)) return;
      // A starter or suggestion sends text that is not the draft; whatever
      // sits in the composer stays there, and is merged behind the question
      // if the send is refused. Sending the draft itself leaves it empty.
      const { draft } = stateRef.current;
      void run(text, raw === draft ? "" : draft);
    },
    [run]
  );

  // Ask the failed or cut-short last turn again. Nothing else: a message the
  // service refused is not this turn, it is in the composer (`resendDraft`).
  const retry = useCallback(() => {
    const current = stateRef.current;
    // The same gate as `send` (turn running, ended, disabled, limit), checked
    // before anything is discarded. Only the busy-session notice is set aside:
    // retrying is exactly how the salon clears it.
    if (!canSend({ ...current, notice: null })) return;
    const last = current.messages[current.messages.length - 1];
    const asked = current.messages[current.messages.length - 2];
    if (!isResendable(last) || asked?.role !== "user") return;
    const text = asked.text.trim();
    // The turn is withdrawn and its question sent again. What the salon typed
    // meanwhile stays in the composer, and is merged behind the question if
    // the send is refused.
    dispatch({ type: "discard_failed_turn" });
    dispatch({ type: "dismiss_notice" });
    if (text === "") return;
    void run(text, current.draft);
  }, [run]);

  // "Try again" on a refusal notice: send the message the service refused,
  // which sits in the composer. Never an older turn's question, even when that
  // turn was cut short or failed (it has its own retry).
  const resendDraft = useCallback(() => {
    const current = stateRef.current;
    if (!canSend({ ...current, notice: null })) return;
    const text = current.draft.trim();
    if (text.length > MAX_MESSAGE_LENGTH) {
      // Sending nothing is not enough: say why. The composer keeps the text.
      dispatch({ type: "draft_too_long" });
      return;
    }
    dispatch({ type: "dismiss_notice" });
    if (text === "") return;
    void run(text, "");
  }, [run]);

  const startNew = useCallback(() => {
    abortRef.current?.abort();
    dispatch({ type: "start_new" });
    setSupplierDetail(null);
  }, []);

  const setDraft = useCallback(
    (draft: string) => dispatch({ type: "set_draft", draft }),
    []
  );
  const dismissNotice = useCallback(
    () => dispatch({ type: "dismiss_notice" }),
    []
  );
  const openSupplierDetail = useCallback(
    (target: SupplierDetailTarget) => setSupplierDetail(target),
    []
  );
  const closeSupplierDetail = useCallback(() => setSupplierDetail(null), []);

  const value = useMemo<OrderAssistantContextValue>(
    () => ({
      state,
      open,
      setOpen,
      sendable: canSend(state),
      send,
      retry,
      resendDraft,
      startNew,
      setDraft,
      dismissNotice,
      supplierDetail,
      openSupplierDetail,
      closeSupplierDetail
    }),
    [
      state,
      open,
      send,
      retry,
      resendDraft,
      startNew,
      setDraft,
      dismissNotice,
      supplierDetail,
      openSupplierDetail,
      closeSupplierDetail
    ]
  );

  return (
    <OrderAssistantContext.Provider value={value}>
      {children}
    </OrderAssistantContext.Provider>
  );
}

export function useOrderAssistant(): OrderAssistantContextValue {
  const context = useContext(OrderAssistantContext);
  if (!context) {
    throw new Error(
      "useOrderAssistant must be used within an OrderAssistantProvider"
    );
  }
  return context;
}
